import { useTranslation } from "@/i18n";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import MetadataBadge from "./components/MetadataBadge";
import MetadataEditModal from "./components/MetadataEditModal";

/**
 * Metadata item structure.
 */
interface MetadataItem {
  readonly id: string;
  readonly name: string;
  readonly title?: string;
  readonly description?: string;
  readonly linkedId?: number;
  readonly parentId?: number;
  readonly extracted_name?: string;
}

/**
 * Props for the MetadataSection component.
 */
interface MetadataSectionProps {
  /** Catalog data containing metadata categories */
  readonly catalog: {
    departments?: MetadataItem[];
    roles?: MetadataItem[];
    resources?: MetadataItem[];
    locations?: MetadataItem[];
    working_groups?: MetadataItem[];
    requirements?: MetadataItem[];
  };
}

/**
 * Edit modal state
 */
interface EditModalState {
  isOpen: boolean;
  categoryKey: string;
  categoryTitle: string;
  itemIndex: number;
  item: MetadataItem | null;
}

/**
 * MetadataSection component displays all metadata categories
 * (departments, roles, resources, etc.) with their items.
 * Supports inline editing of metadata items via modal.
 *
 * @param props - Component props
 * @returns Metadata section UI
 */
export default function MetadataSection({ catalog }: MetadataSectionProps) {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const [editModal, setEditModal] = useState<EditModalState>({
    isOpen: false,
    categoryKey: "",
    categoryTitle: "",
    itemIndex: -1,
    item: null,
  });

  // Read dropdown data from localStorage for resource groups, orgcharts, and requirement_min_chapters
  const dropdownDataStr = localStorage.getItem("aiio_dropdown_data");
  const dropdownData = dropdownDataStr ? JSON.parse(dropdownDataStr) : null;
  const resourceGroups = dropdownData?.data?.resourcegroups || [];
  const orgCharts = (dropdownData?.data?.orgcharts || []).filter(
    (chart: any) => chart.org_full_model_id != null
  );
  const requirementMinChapters =
    dropdownData?.data?.requirement_min_chapters || [];

  // Watch form values to get real-time updates
  const formDepartments = watch("departments");
  const formRoles = watch("roles");
  const formResources = watch("resources");
  const formLocations = watch("locations");
  const formWorkingGroups = watch("working_groups");
  const formRequirements = watch("requirements");

  // Use form data if available, fallback to catalog prop
  const catalogData = {
    departments: formDepartments || catalog?.departments || [],
    roles: formRoles || catalog?.roles || [],
    resources: formResources || catalog?.resources || [],
    locations: formLocations || catalog?.locations || [],
    working_groups: formWorkingGroups || catalog?.working_groups || [],
    requirements: formRequirements || catalog?.requirements || [],
  };

  const metadataCategories = [
    {
      key: "departments",
      label: t("documentReview.metadata.departments"),
      singularLabel: t("documentReview.metadata.department"),
    },
    {
      key: "roles",
      label: t("documentReview.metadata.roles"),
      singularLabel: t("documentReview.metadata.role"),
    },
    {
      key: "resources",
      label: t("documentReview.metadata.resources"),
      singularLabel: t("documentReview.metadata.resource"),
    },
    {
      key: "locations",
      label: t("documentReview.metadata.locations"),
      singularLabel: t("documentReview.metadata.location"),
    },
    {
      key: "working_groups",
      label: t("documentReview.metadata.workgroups"),
      singularLabel: t("documentReview.metadata.workgroup"),
    },
    {
      key: "requirements",
      label: t("documentReview.metadata.requirements"),
      singularLabel: t("documentReview.metadata.requirement"),
    },
  ] as const;

  const handleEditClick = (
    categoryKey: string,
    categoryTitle: string,
    itemIndex: number,
    item: MetadataItem
  ) => {
    setEditModal({
      isOpen: true,
      categoryKey,
      categoryTitle,
      itemIndex,
      item,
    });
  };

  const handleCloseModal = () => {
    setEditModal({
      isOpen: false,
      categoryKey: "",
      categoryTitle: "",
      itemIndex: -1,
      item: null,
    });
  };

  // Get parent name for an item (resources, departments, roles, requirements)
  const getParentName = (
    categoryKey: string,
    parentId?: number
  ): string | undefined => {
    if (!parentId) return undefined;

    if (categoryKey === "resources") {
      const parent = resourceGroups.find((rg: any) => rg.id === parentId);
      return parent?.name;
    } else if (categoryKey === "departments" || categoryKey === "roles") {
      const parent = orgCharts.find((chart: any) => chart.id === parentId);
      return parent?.name;
    } else if (categoryKey === "requirements") {
      const parent = requirementMinChapters.find(
        (req: any) => req.id === parentId
      );
      return parent?.name;
    }
    return undefined;
  };

  return (
    <>
      <div className="space-y-4">
        <h3 className="text-base font-semibold">
          {t("documentReview.linkedMetadataTitle")}
        </h3>
        <div className="flex flex-wrap gap-8">
          {metadataCategories.map(({ key, label, singularLabel }) => {
            const items = Array.isArray(catalogData?.[key])
              ? catalogData[key]
              : [];

            return (
              <div key={key} className="space-y-2">
                <h4 className="text-sm font-semibold">{label}</h4>
                <div className="flex flex-wrap gap-2">
                  {items.length > 0 ? (
                    <>
                      {items.map((item, idx) => {
                        const parentName = getParentName(key, item?.parentId);
                        return (
                          <MetadataBadge
                            key={item?.id || `item-${idx}`}
                            name={item?.name || item?.title || t("common.unknown")}
                            description={item?.description}
                            isLinked={!!item?.linkedId}
                            hasParent={!!item?.parentId}
                            parentName={parentName}
                            extractedName={item?.extracted_name}
                            onEdit={() =>
                              handleEditClick(key, singularLabel, idx, item)
                            }
                          />
                        );
                      })}
                    </>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-1 bg-[#ffffff] border border-[#e4e4e7] text-sm text-[#3f3f46] font-semibold rounded-lg">
                      -
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit Modal */}
      {editModal.isOpen && editModal.item && (
        <MetadataEditModal
          isOpen={editModal.isOpen}
          onClose={handleCloseModal}
          categoryTitle={editModal.categoryTitle}
          categoryKey={editModal.categoryKey}
          itemIndex={editModal.itemIndex}
          item={editModal.item}
        />
      )}
    </>
  );
}
